import { Link } from "react-router-dom";
import { LegalPage, Sec, type TocEntry } from "../components/LegalPage";
import { COMPANY, PLATFORM_LINKS } from "../lib/company";
import { EmailLink } from "../components/EmailLink";
import { DeleteAccountForm } from "../components/DeleteAccountForm";

const TOC: TocEntry[] = [
  { id: "hold", title: "What We Hold" },
  { id: "inapp", title: "Delete Individual Items" },
  { id: "disconnect", title: "Disconnect a Social Account" },
  { id: "revoke", title: "Revoke Access at Facebook, Instagram or Google" },
  { id: "account", title: "Delete Your Account and All Data" },
  { id: "timeline", title: "What Happens and When" },
  { id: "exceptions", title: "What We Cannot Delete" },
  { id: "contact", title: "Contact" },
];

export function DataDeletion() {
  const mailto =
    `mailto:${COMPANY.privacyEmail}` +
    `?subject=${encodeURIComponent("Delete my account")}` +
    `&body=${encodeURIComponent(
      "Please delete my Simino Social account and all associated data.\n\n" +
        "Registered email address: \n" +
        "Workspace name (if known): \n",
    )}`;

  return (
    <div className="wrap wrap-legal">
      <LegalPage
        title="Delete your data"
        toc={TOC}
        intro={<>
          This page explains exactly how to remove your data from {COMPANY.product}, how to revoke
          our access to your Facebook, Instagram and YouTube accounts, and how long each step takes.
          The in-app routes below are available without contacting us; email is available as a
          fallback for full account deletion.
        </>}
      >
        <Sec id="hold" n={1} title="What We Hold">
          <p>For an active account, {COMPANY.product} stores:</p>
          <ul>
            <li>your email address and user record;</li>
            <li>your workspace and its membership;</li>
            <li>the photos and videos you uploaded, in a private storage bucket;</li>
            <li>your posts — captions, titles, descriptions, schedules and target selections;</li>
            <li>one delivery record per post per account, with its status and the identifier the
              platform returned;</li>
            <li>for each connected account, its identifier, display name and profile image;</li>
            <li>for each connected account, an encrypted access or refresh token.</li>
          </ul>
          <p>
            We do not hold your Facebook, Instagram or Google password. We never receive it. See the{" "}
            <Link to="/privacy">Privacy Policy</Link> for the full description.
          </p>
        </Sec>

        <Sec id="inapp" n={2} title="Delete Individual Items">
          <p>You can remove specific content without deleting your account:</p>
          <ol>
            <li>Sign in to {COMPANY.product}.</li>
            <li>Open <strong>Posts</strong>.</li>
            <li>Open the post you want to remove and choose <strong>Delete</strong>.</li>
          </ol>
          <p>
            Deleting a post removes its caption, schedule, delivery records and the uploaded media
            attached to it from our systems. If the post was already published, the copy on Facebook,
            Instagram or YouTube is unaffected — delete that on the platform itself.
          </p>
        </Sec>

        <Sec id="disconnect" n={3} title="Disconnect a Social Account">
          <p>This is the fastest way to end our access to one platform:</p>
          <ol>
            <li>Sign in to {COMPANY.product}.</li>
            <li>Open <strong>Connections</strong>.</li>
            <li>Find the Page, Instagram account or YouTube channel and choose{" "}
              <strong>Disconnect</strong>.</li>
          </ol>
          <p>
            The stored token for that account is deleted immediately and permanently. We can no
            longer publish to it or read anything from it. Any scheduled posts targeting that account
            will fail rather than publish.
          </p>
        </Sec>

        <Sec id="revoke" n={4} title="Revoke Access at Facebook, Instagram or Google">
          <p>
            You can cut off our access at the source, without signing in to {COMPANY.product} at all.
          </p>
          <p><strong>Facebook and Instagram</strong></p>
          <ol>
            <li>Go to{" "}
              <a href={PLATFORM_LINKS.metaAppSettings} target="_blank" rel="noreferrer noopener">
                Facebook Settings &rarr; Business Integrations
              </a>.</li>
            <li>Find <strong>{COMPANY.product}</strong> in the list.</li>
            <li>Choose <strong>Remove</strong>, and tick the option to delete the posts and activity
              the app created if you also want those removed from Facebook.</li>
          </ol>
          <p><strong>YouTube and Google</strong></p>
          <ol>
            <li>Go to your{" "}
              <a href={PLATFORM_LINKS.googlePermissions} target="_blank" rel="noreferrer noopener">
                Google security settings page
              </a>{" "}
              at myaccount.google.com/permissions.</li>
            <li>Select <strong>{COMPANY.product}</strong> under third-party apps with account access.</li>
            <li>Choose <strong>Remove Access</strong>.</li>
          </ol>
          <p>
            Revoking at the platform invalidates the token instantly. To also delete the record we
            hold about that connection, disconnect it in {COMPANY.product} as in section 3, or email
            us as in section 5.
          </p>
        </Sec>

        <Sec id="account" n={5} title="Delete Your Account and All Data">
          <p>
            To delete your {COMPANY.product} account together with every post, media file, delivery
            record and stored credential, sign in and use the form on this page, or open{" "}
            <strong>Settings → Delete Account</strong> in the mobile app. Confirm the warning and
            choose <strong>Delete my account</strong>. We record your request immediately, sign you
            out on every device, and the account can no longer sign in.
          </p>
          <DeleteAccountForm />
          <p>If the request cannot be submitted:</p>
          <ol>
            <li>Send an email from your <strong>registered email address</strong> to{" "}
              <EmailLink address={COMPANY.privacyEmail} />.</li>
            <li>Use the subject line <strong>Delete my account</strong>.</li>
            <li>Include your registered email address and, if you have more than one, the workspace
              name.</li>
          </ol>
          <p>
            <EmailLink address={COMPANY.privacyEmail} href={mailto}
                       label="Email a deletion request" className="btn-link primary" />
          </p>
          <p className="muted">
            We use the registered email address to verify the request. If you no longer control that
            mailbox, write from any address and we will verify your identity another way before
            acting.
          </p>
        </Sec>

        <Sec id="timeline" n={6} title="What Happens and When">
          <ul>
            <li><strong>Immediately</strong> — we record your in-app request and the account stops
              being able to sign in, or we acknowledge an email request by email.</li>
            <li><strong>Within 7 days</strong> — all stored access and refresh tokens for your
              connected accounts are deleted, and our ability to publish on your behalf ends.</li>
            <li><strong>Within 30 days</strong> — your user record, workspace, posts, delivery
              records and uploaded media are deleted from our live systems.</li>
            <li><strong>Within 90 days</strong> — the data ages out of encrypted infrastructure
              backups, which are not accessible to the application and are overwritten on a rolling
              schedule.</li>
          </ul>
          <p>
            We send a written confirmation when the deletion is complete. If we refuse any part of a
            request we will tell you which part and why.
          </p>
        </Sec>

        <Sec id="exceptions" n={7} title="What We Cannot Delete">
          <ul>
            <li><strong>Content already published.</strong> A photo on your Facebook Page or a video
              on your YouTube channel belongs to that platform once published. Delete it there.</li>
            <li><strong>Records we are required to keep.</strong> A limited set of records may be
              retained where necessary to comply with legal, tax or accounting obligations, to resolve
              a dispute, or to prevent fraud and abuse. These are kept only as long as that purpose
              requires and are not used for anything else.</li>
          </ul>
        </Sec>

        <Sec id="contact" n={8} title="Contact">
          <p className="contact-block">
            {COMPANY.legalName}<br />
            {COMPANY.address.oneLine}<br />
            Email: <EmailLink address={COMPANY.privacyEmail} /><br />
            Phone: <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}>{COMPANY.phone}</a>
          </p>
          <p className="muted">
            Grievances under India&rsquo;s Digital Personal Data Protection Act, 2023 may be addressed
            to the Grievance Officer at the same address. See{" "}
            <Link to="/privacy#grievance">Privacy Policy &sect; 20</Link>.
          </p>
        </Sec>
      </LegalPage>
    </div>
  );
}
